/**
 * Даты в переписке: разметка сообщений по дням для разделителей в стиле
 * Telegram («Сегодня», «Вчера», «3 ноября», «3 ноября 2025»).
 */

/** Полночь календарного дня момента времени, в миллисекундах (локальная зона). */
const startOfDay = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * Момент отправки сообщения. Бэкенд отдаёт `sentAt`, но у оптимистичных и
 * локально добавленных сообщений поле может называться иначе — проверяем
 * несколько вариантов и не падаем на мусорных значениях.
 */
export const messageDate = (message: any): Date | null => {
    const raw = message?.sentAt || message?.createdAt || message?.timestamp || message?.payload?.sentAt;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Относятся ли два момента к одному календарному дню. */
export const isSameDay = (left: Date, right: Date): boolean => startOfDay(left) === startOfDay(right);

/**
 * Подпись разделителя дат. `now` параметром — чтобы подпись можно было
 * пересчитать и проверить без подмены системных часов.
 */
export const formatChatDate = (date: Date, now: Date = new Date()): string => {
    const today = startOfDay(now);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    const day = startOfDay(date);

    if (day === today) return 'Сегодня';
    if (day === yesterday) return 'Вчера';

    // Год показываем только для прошлых лет — как в Telegram.
    return date.toLocaleDateString(
        'ru-RU',
        date.getFullYear() === now.getFullYear()
            ? { day: 'numeric', month: 'long' }
            : { day: 'numeric', month: 'long', year: 'numeric' },
    );
};

/**
 * Короткое время для списка чатов: сегодня — «14:32», вчера — «Вчера»,
 * раньше — «12.03» (с годом, если он не текущий). Место в строке чата
 * ограничено, поэтому дата не разворачивается в «3 ноября».
 */
export const formatChatListTime = (value: string | null | undefined, now: Date = new Date()): string => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const today = startOfDay(now);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    const day = startOfDay(date);

    if (day === today) {
        return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    if (day === yesterday) return 'Вчера';

    const parts = [
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
    ];

    if (date.getFullYear() !== now.getFullYear()) {
        parts.push(String(date.getFullYear()));
    }

    return parts.join('.');
};
